/**
 * SoundBYTE Originals Configuration
 *
 * This file contains all configurable options for the SoundBYTE Originals product.
 * Prices are in USD. Update this file to change pricing without code changes.
 */

// ============================================
// TIER CONFIGURATION
// ============================================

export interface OriginalsTierUpgrade {
  id: string;
  label: string;
  price: number;
}

export interface OriginalsTierConfig {
  id: 'low' | 'medium' | 'high';
  name: string;
  tagline: string;
  priceDisplay: string;
  priceMin: number;
  priceMax: number;
  retainerAmount: number;
  features: string[];
  materialIncluded: string;
  digitalIncluded: boolean;
  revisions: number | 'unlimited';
  upgradeOptions: OriginalsTierUpgrade[];
  flowType: 'simple' | 'collaborative';
}

export const ORIGINALS_TIERS: OriginalsTierConfig[] = [
  {
    id: 'low',
    name: 'Low Customisation',
    tagline: 'Artist-led SoundBYTE Original',
    priceDisplay: '$2,500',
    priceMin: 2500,
    priceMax: 2500,
    retainerAmount: 0,
    features: [
      'Up to 5 uploaded images',
      '1 personal image integrated',
      'Fixed scope',
      'No revisions',
    ],
    materialIncluded: 'Medium Canvas (Framed)',
    digitalIncluded: false,
    revisions: 0,
    upgradeOptions: [
      { id: 'digital', label: 'Digital add-on', price: 250 },
      { id: 'medium-aluminium', label: 'Medium Aluminium', price: 200 },
      { id: 'large-aluminium', label: 'Large Aluminium', price: 500 },
      { id: 'additional-colorways', label: 'Additional Colorways', price: 250 },
    ],
    flowType: 'simple',
  },
  {
    id: 'medium',
    name: 'Medium Customisation',
    tagline: 'Deeper narrative development',
    priceDisplay: '$4,000 - $10,000',
    priceMin: 4000,
    priceMax: 10000,
    retainerAmount: 3000,
    features: [
      'Deeper narrative development',
      'Layered composition',
      'Multiple refinements',
      'Digital included',
    ],
    materialIncluded: 'Medium Canvas (Framed)',
    digitalIncluded: true,
    revisions: 2,
    upgradeOptions: [
      { id: 'medium-aluminium', label: 'Medium Aluminium', price: 200 },
      { id: 'large-aluminium', label: 'Large Aluminium', price: 500 },
    ],
    flowType: 'collaborative',
  },
  {
    id: 'high',
    name: 'High Customisation',
    tagline: 'Flagship / legacy / brand-level work',
    priceDisplay: '$10,000 - $25,000+',
    priceMin: 10000,
    priceMax: 25000,
    retainerAmount: 3000,
    features: [
      'Flagship / legacy / brand-level work',
      'Extended collaboration',
      'Unlimited refinements',
      'Digital included',
      'Priority support',
    ],
    materialIncluded: 'Medium Canvas (Framed)',
    digitalIncluded: true,
    revisions: 'unlimited',
    upgradeOptions: [
      { id: 'medium-aluminium', label: 'Medium Aluminium', price: 200 },
      { id: 'large-aluminium', label: 'Large Aluminium', price: 500 },
    ],
    flowType: 'collaborative',
  },
];

export type OriginalsTierId = OriginalsTierConfig['id'];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get tier configuration by ID
 */
export function getOriginalsTier(tierId: OriginalsTierId): OriginalsTierConfig | undefined {
  return ORIGINALS_TIERS.find((tier) => tier.id === tierId);
}

/**
 * Check if tier requires a retainer
 */
export function tierRequiresRetainer(tierId: OriginalsTierId): boolean {
  const tier = getOriginalsTier(tierId);
  return tier ? tier.retainerAmount > 0 : false;
}

/**
 * Get retainer amount for a tier
 */
export function getTierRetainerAmount(tierId: OriginalsTierId): number {
  const tier = getOriginalsTier(tierId);
  return tier?.retainerAmount ?? 0;
}

/**
 * Calculate total price with upgrades for Low tier
 */
export function calculateLowTierPrice(selectedUpgrades: string[]): {
  basePrice: number;
  upgradesPrice: number;
  total: number;
  breakdown: { label: string; amount: number }[];
} {
  const tier = getOriginalsTier('low');
  if (!tier) {
    throw new Error('Low tier configuration not found');
  }

  const basePrice = tier.priceMin;
  const breakdown: { label: string; amount: number }[] = [
    { label: 'SoundBYTE Original (Low)', amount: basePrice },
  ];

  let upgradesPrice = 0;

  for (const upgradeId of selectedUpgrades) {
    const upgrade = tier.upgradeOptions.find((u) => u.id === upgradeId);
    if (upgrade) {
      upgradesPrice += upgrade.price;
      breakdown.push({ label: upgrade.label, amount: upgrade.price });
    }
  }

  return {
    basePrice,
    upgradesPrice,
    total: basePrice + upgradesPrice,
    breakdown,
  };
}

// ============================================
// VALIDATION RULES
// ============================================

export const ORIGINALS_VALIDATION = {
  // Low tier
  lowTier: {
    maxReferenceImages: 3,
    personalImageRequired: true,
    voiceRequired: true,
    voiceMaxDuration: 120, // seconds
  },
  // Medium tier
  mediumTier: {
    maxReferenceImages: 10,
    projectNameMinLength: 1,
    projectNameMaxLength: 200,
    descriptionMinLength: 10,
    descriptionMaxLength: 2000,
    voiceRequired: true,
    voiceMaxDuration: 120, // seconds
  },
  // High tier
  highTier: {
    maxReferenceImages: 25,
    projectNameMinLength: 1,
    projectNameMaxLength: 200,
    descriptionMinLength: 10,
    descriptionMaxLength: 2000,
    voiceRequired: true,
    voiceMaxDuration: 120, // seconds
  },
  // Legacy - kept for backwards compatibility
  collaborativeTier: {
    projectNameMinLength: 1,
    projectNameMaxLength: 200,
    descriptionMinLength: 10,
    descriptionMaxLength: 2000,
    inspirationMinLength: 10,
    inspirationMaxLength: 2000,
    maxReferenceFiles: 10,
    voiceRequired: true,
    voiceMaxDuration: 120, // seconds
  },
};

// ============================================
// CONTENT STRINGS
// ============================================

export const ORIGINALS_CONTENT = {
  tierSelection: {
    title: 'Choose Your Customisation Level',
    subtitle: 'Select the tier that best fits your vision and budget',
    retainerNote:
      'Medium & High tiers require a $3,000 retainer to begin. Final price determined after consultation.',
  },
  lowTier: {
    inputTitle: 'Create Your SoundBYTE',
    inputSubtitle: 'Record your voice and share your inspiration',
    commissionNameLabel: 'What do you want to name your commission?',
    commissionNameDescription: 'Give your artwork a meaningful name',
    commissionNamePlaceholder: 'e.g., "My Family Legacy", "Golden Memories"',
    voiceLabel: 'Voice Recording',
    voiceDescription: 'Your voice becomes the visual foundation of your SoundBYTE artwork',
    referenceImagesLabel: 'Reference Images (up to 3)',
    referenceImagesDescription: 'Share images that inspire the direction of your artwork',
    personalImageLabel: 'Personal Image (required)',
    personalImageDescription: 'Upload a personal photo to integrate into your SoundBYTE artwork',
    briefLabel: 'Brief Description (optional)',
    briefDescription: 'Any additional context for the artist',
  },
  reviewStep: {
    lowTierTitle: 'Review & Submit',
    lowTierSubtitle: 'Review your submission and complete payment',
    upgradesLabel: 'Optional Upgrades',
  },
};
